import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, TrendingUp, Calendar, RotateCw } from "lucide-react";
import { getMonthRange, formatDateForDisplay } from "@/lib/dateUtils";

interface CalculationResult {
  coupon: string;
  success: boolean;
  commission_amount?: number;
  orders_count?: number;
  error?: string;
}

interface CalculationResponse {
  message: string;
  results: CalculationResult[];
}

interface CouponOption {
  coupon: string;
  name: string;
  email: string;
}

interface CalculationRequestBody {
  month_year: string;
  period_start: string;
  period_end: string;
  coupon?: string;
}

interface BackfillMonthResult {
  month_year: string;
  success: boolean;
  message?: string;
  results?: CalculationResult[];
  error?: string;
}

interface BackfillResponse {
  months_processed: number;
  results: BackfillMonthResult[];
}

const MONTHS_PT = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

export const AdminCommissionCalculator: React.FC = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // 1-based

  const [selectedYear, setSelectedYear] = useState<number>(2026); // Default to 2026
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [coupon, setCoupon] = useState("");
  const [coupons, setCoupons] = useState<CouponOption[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isLoadingCoupons, setIsLoadingCoupons] = useState(true);
  const [results, setResults] = useState<CalculationResponse | null>(null);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillResults, setBackfillResults] = useState<BackfillResponse | null>(null);
  const [backfillProgress, setBackfillProgress] = useState("");

  // Auto-calculate period based on selected month/year
  const { periodStart, periodEnd, monthYear } = useMemo(() => {
    const range = getMonthRange(selectedYear, selectedMonth);
    const monthYearStr = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}`;
    return {
      periodStart: range.start,
      periodEnd: range.end,
      monthYear: monthYearStr,
    };
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await apiGet("/api/commission-balances/coupons");
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      } else {
        console.error("Error fetching coupons");
        toast.error("Erro ao carregar cupons");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erro ao carregar cupons");
    } finally {
      setIsLoadingCoupons(false);
    }
  };

  const handleCalculate = async () => {
    setIsCalculating(true);
    try {
      const requestBody: CalculationRequestBody = {
        month_year: monthYear,
        period_start: periodStart,
        period_end: periodEnd,
      };

      // Add coupon if specified
      if (coupon.trim()) {
        requestBody.coupon = coupon.trim().toUpperCase();
      }

      const response = await apiPost("/api/commission-balances/calculate", requestBody);

      if (response.ok) {
        const data: CalculationResponse = await response.json();
        setResults(data);
        toast.success(data.message);
      } else {
        const errorData = await response.text();
        console.error("API Error:", errorData);
        toast.error("Erro ao calcular comissões. Verifique o console para detalhes.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erro ao calcular comissões");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleBackfill = async () => {
    setIsBackfilling(true);
    setBackfillResults(null);
    setBackfillProgress("Gerando lista de meses...");

    try {
      // Build months list from July 2025 to current month
      const months: string[] = [];
      const now = new Date();
      const endYear = now.getFullYear();
      const endMonth = now.getMonth() + 1;
      let year = 2025;
      let month = 7;

      while (year < endYear || (year === endYear && month <= endMonth)) {
        months.push(`${year}-${String(month).padStart(2, "0")}`);
        month++;
        if (month > 12) {
          month = 1;
          year++;
        }
      }

      setBackfillProgress(`Recalculando ${months.length} meses: ${months[0]} a ${months[months.length - 1]}...`);

      const response = await apiPost(
        `/api/commission-balances/calculate-all?months=${months.join(",")}`,
        {}
      );

      if (response.ok) {
        const data: BackfillResponse = await response.json();
        setBackfillResults(data);
        const successCount = data.results.filter((r) => r.success).length;
        toast.success(`Backfill concluído: ${successCount}/${data.months_processed} meses processados`);
      } else {
        const errorText = await response.text();
        console.error("Backfill error:", errorText);
        toast.error("Erro ao executar backfill. Verifique o console.");
      }
    } catch (error) {
      console.error("Backfill error:", error);
      toast.error("Erro ao executar backfill");
    } finally {
      setIsBackfilling(false);
      setBackfillProgress("");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Calculadora de Comissões
          </CardTitle>
          <CardDescription>Calcule e armazene as comissões mensais de todos os influenciadores</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="year">Ano</Label>
              <Select
                value={selectedYear.toString()}
                onValueChange={(value) => setSelectedYear(parseInt(value, 10))}
              >
                <SelectTrigger id="year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="month">Mês</Label>
              <Select
                value={selectedMonth.toString()}
                onValueChange={(value) => setSelectedMonth(parseInt(value, 10))}
              >
                <SelectTrigger id="month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_PT.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="coupon">Cupom (Opcional)</Label>
              <Select 
                onValueChange={(value) => setCoupon(value === "ALL_COUPONS" ? "" : value)} 
                value={coupon || "ALL_COUPONS"} 
                disabled={isLoadingCoupons}
              >
                <SelectTrigger id="coupon" className="w-full">
                  <SelectValue placeholder="Selecione um cupom" />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingCoupons ? (
                    <SelectItem value="loading" disabled>
                      Carregando cupons...
                    </SelectItem>
                  ) : (
                    <>
                      <SelectItem value="ALL_COUPONS">Todos os cupons</SelectItem>
                      {coupons.map((couponOption) => (
                        <SelectItem key={couponOption.coupon} value={couponOption.coupon}>
                          {couponOption.coupon} - {couponOption.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Deixe vazio para calcular todos</p>
            </div>
          </div>

          {/* Auto-calculated period display */}
          <div className="p-4 bg-muted/50 rounded-lg border">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Período Calculado</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Mês/Ano:</span>{" "}
                <span className="font-medium">
                  {MONTHS_PT.find((m) => m.value === selectedMonth)?.label} {selectedYear}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Data Início:</span>{" "}
                <span className="font-medium">{formatDateForDisplay(periodStart)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Data Fim:</span>{" "}
                <span className="font-medium">{formatDateForDisplay(periodEnd)}</span>
              </div>
            </div>
          </div>
          <Button onClick={handleCalculate} disabled={isCalculating} className="w-full">
            {isCalculating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Calculando...
              </>
            ) : (
              <>
                <Calculator className="w-4 h-4 mr-2" />
                {coupon.trim() ? `Calcular Cupom ${coupon.trim().toUpperCase()}` : "Calcular Todos os Cupons"}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Resultados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {results.results.filter((r: CalculationResult) => r.success).length}
                  </div>
                  <div className="text-sm text-green-600">Sucessos</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {results.results.filter((r: CalculationResult) => !r.success).length}
                  </div>
                  <div className="text-sm text-red-600">Falhas</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{results.results.length}</div>
                  <div className="text-sm text-blue-600">Total</div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Detalhes:</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {results.results.map((result: CalculationResult, index: number) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg text-sm ${
                        result.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                      }`}
                    >
                      <div className="font-medium">
                        {result.coupon}: {result.success ? "Sucesso" : "Falha"}
                      </div>
                      {result.success && (
                        <div className="text-xs mt-1">
                          Comissão: R$ {result.commission_amount?.toFixed(2) || "0.00"} | Pedidos:{" "}
                          {result.orders_count || 0}
                        </div>
                      )}
                      {!result.success && <div className="text-xs mt-1">Erro: {result.error}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backfill Section */}
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCw className="h-5 w-5" />
            Backfill Histórico
          </CardTitle>
          <CardDescription>
            Recalcula as comissões de todos os meses desde Julho/2025 até o mês atual.
            Isso captura pedidos que foram completados após o fechamento do mês original.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
            <p className="font-medium mb-1">Seguro para executar:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Não duplica dados — atualiza registros existentes (upsert)</li>
              <li>Comissões só podem aumentar (mais pedidos completados)</li>
              <li>Saques existentes não são afetados</li>
            </ul>
          </div>

          <Button
            onClick={handleBackfill}
            disabled={isBackfilling}
            variant="outline"
            className="w-full border-amber-300 hover:bg-amber-50"
          >
            {isBackfilling ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600 mr-2"></div>
                {backfillProgress || "Processando..."}
              </>
            ) : (
              <>
                <RotateCw className="w-4 h-4 mr-2" />
                Executar Backfill (Jul/2025 até hoje)
              </>
            )}
          </Button>

          {backfillResults && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">
                    {backfillResults.results.filter((r) => r.success).length}
                  </div>
                  <div className="text-xs text-green-600">Meses com sucesso</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-xl font-bold text-red-600">
                    {backfillResults.results.filter((r) => !r.success).length}
                  </div>
                  <div className="text-xs text-red-600">Meses com falha</div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-sm">Resultado por mês:</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {backfillResults.results.map((monthResult, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg text-sm ${
                        monthResult.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"
                      }`}
                    >
                      <div className="font-medium">
                        {monthResult.month_year}: {monthResult.success ? "Sucesso" : "Falha"}
                      </div>
                      {monthResult.success && monthResult.message && (
                        <div className="text-xs mt-1">{monthResult.message}</div>
                      )}
                      {!monthResult.success && (
                        <div className="text-xs mt-1">Erro: {monthResult.error}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
